import { Controller } from "stimulus"

// Drives the Voucher Type Mapping table:
//   - filters each row's Series dropdown to the row's selected Voucher Type
//   - enables the "Edit series" button only when a manual series is selected
//   - opens a right-side drawer to add or edit a manual series, with a live number preview
//
// The drawer's form is a plain HTML form whose action/method are swapped at open time:
//   edit   -> PATCH  seriesUrlTemplate (SERIES_ID replaced)
//   create -> POST   createUrl
export default class extends Controller {
  static values = {
    seriesMeta:        Object, // { [seriesId]: { manual, name, prefix, suffix, ... next_number } }
    seriesUrlTemplate: String, // member PATCH url containing the SERIES_ID placeholder
    createUrl:         String  // collection POST url for a new series
  }

  connect() {
    this.meta = this.seriesMetaValue || {}
    this.setupRows()
    this.setupDrawer()

    this.onKeydown = (event) => { if (event.key === "Escape") this.close() }
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
  }

  // ---- Table rows -------------------------------------------------------------

  setupRows() {
    this.element.querySelectorAll("[data-vtm-row]").forEach((row) => {
      const typeSelect   = row.querySelector("[data-vtm-voucher-type]")
      const seriesSelect = row.querySelector("[data-vtm-series]")
      const editBtn      = row.querySelector("[data-vtm-edit]")
      const addBtn       = row.querySelector("[data-vtm-add]")
      const mappingId    = row.getAttribute("data-mapping-id")
      if (!typeSelect || !seriesSelect) return

      const refreshEditButton = () => {
        if (!editBtn) return
        // Enabled only when a manual series is selected; disabled otherwise (no series
        // selected, or the selected series is not manual).
        const meta = this.meta[seriesSelect.value]
        editBtn.disabled = !(meta && meta.manual)
      }

      const syncSeries = () => {
        const typeId = typeSelect.value
        let selectedStillValid = false
        Array.from(seriesSelect.options).forEach((opt) => {
          if (!opt.value) return // keep the "None" option
          const matches = opt.getAttribute("data-voucher-type-id") === typeId
          opt.hidden   = !matches
          opt.disabled = !matches
          if (matches && opt.selected) selectedStillValid = true
        })
        if (!selectedStillValid) seriesSelect.value = ""
        refreshEditButton()
      }

      typeSelect.addEventListener("change", syncSeries)
      seriesSelect.addEventListener("change", refreshEditButton)

      if (editBtn) {
        editBtn.addEventListener("click", () => {
          const meta = this.meta[seriesSelect.value]
          if (meta && meta.manual) this.openEdit(meta)
        })
      }

      if (addBtn) {
        addBtn.addEventListener("click", () => {
          const opt = typeSelect.options[typeSelect.selectedIndex]
          this.openCreate({
            voucherTypeId:   typeSelect.value,
            voucherTypeName: opt ? opt.text : "",
            mappingId:       mappingId
          })
        })
      }

      refreshEditButton()
    })
  }

  // ---- Drawer -----------------------------------------------------------------

  setupDrawer() {
    this.drawer  = this.element.querySelector("[data-vtm-drawer]")
    this.overlay = this.element.querySelector("[data-vtm-overlay]")
    if (!this.drawer) return

    this.form        = this.drawer.querySelector("form")
    this.nameEl      = this.drawer.querySelector("[data-vtm-series-name]")
    this.nextEl      = this.drawer.querySelector("[data-vtm-next-number]")
    this.previewEl   = this.drawer.querySelector("[data-vtm-preview]")
    this.titleEl     = this.drawer.querySelector("[data-vtm-title]")
    this.submitEl    = this.drawer.querySelector("[data-vtm-submit]")
    this.methodInput = this.drawer.querySelector("[data-vtm-method]")
    this.typeIdInput = this.drawer.querySelector("[data-vtm-voucher-type-id]")
    this.mapIdInput  = this.drawer.querySelector("[data-vtm-mapping-id]")

    ;["use_custom_manual_series", "prefix", "suffix", "starting_number", "numerical_width"].forEach((name) => {
      const el = this.field(name)
      if (!el) return
      el.addEventListener("input", () => this.buildPreview())
      el.addEventListener("change", () => { this.toggleCustomFields(); this.buildPreview() })
    })

    this.drawer.querySelectorAll("[data-vtm-drawer-close]").forEach((btn) => {
      btn.addEventListener("click", () => this.close())
    })
    if (this.overlay) this.overlay.addEventListener("click", () => this.close())
  }

  field(name) { return this.drawer.querySelector(`[data-vtm-field="${name}"]`) }

  setValue(name, value) {
    const el = this.field(name)
    if (!el) return
    if (el.type === "checkbox") el.checked = !!value
    else el.value = (value === null || value === undefined) ? "" : value
  }

  fill(values) {
    this.setValue("name", values.name)
    this.setValue("use_custom_manual_series", values.use_custom_manual_series)
    this.setValue("prefix", values.prefix)
    this.setValue("suffix", values.suffix)
    this.setValue("starting_number", values.starting_number)
    this.setValue("numerical_width", values.numerical_width)
    this.setValue("prefill_zero", values.prefill_zero)
    this.setValue("prevent_duplicates", values.prevent_duplicates)
    this.toggleCustomFields()
    this.buildPreview()
  }

  // Live "format preview": prefix/suffix only apply in custom mode; number is padded to width.
  buildPreview() {
    const custom = this.field("use_custom_manual_series").checked
    let start = parseInt(this.field("starting_number").value, 10)
    if (isNaN(start) || start === 0) start = 1
    const width = parseInt(this.field("numerical_width").value, 10) || 0
    let num = String(start)
    if (width > 0) num = num.padStart(width, "0")
    const prefix = custom ? (this.field("prefix").value || "") : ""
    const suffix = custom ? (this.field("suffix").value || "") : ""
    this.previewEl.textContent = prefix + num + suffix
  }

  toggleCustomFields() {
    const custom = this.field("use_custom_manual_series").checked
    ;["prefix", "suffix"].forEach((name) => {
      const el = this.field(name)
      el.disabled = !custom
      el.closest(".field").classList.toggle("vtm-field-disabled", !custom)
    })
  }

  show()  { this.drawer.classList.add("is-open");    if (this.overlay) this.overlay.classList.add("is-open") }
  close() { this.drawer.classList.remove("is-open"); if (this.overlay) this.overlay.classList.remove("is-open") }

  // Edit an existing manual series (PATCH the member route).
  openEdit(meta) {
    this.form.action         = this.seriesUrlTemplateValue.replace("SERIES_ID", meta.id)
    this.methodInput.value   = "patch"
    this.typeIdInput.value   = ""
    this.mapIdInput.value    = ""
    this.titleEl.textContent  = "Edit Series"
    this.submitEl.textContent = "Save changes"
    this.nameEl.textContent   = meta.name || ""
    this.nextEl.textContent   = meta.next_number || "—"
    this.fill(meta)
    this.show()
  }

  // Create a new manual series for the row's voucher type (POST the collection route).
  openCreate(opts) {
    this.form.action         = this.createUrlValue
    this.methodInput.value   = "" // plain POST (Rails ignores an empty _method)
    this.typeIdInput.value   = opts.voucherTypeId || ""
    this.mapIdInput.value    = opts.mappingId || ""
    this.titleEl.textContent  = "Add Series"
    this.submitEl.textContent = "Create series"
    this.nameEl.textContent   = opts.voucherTypeName ? `New series for ${opts.voucherTypeName}` : "New series"
    this.nextEl.textContent   = "—"
    this.fill({
      name: "", use_custom_manual_series: false, prefix: "", suffix: "",
      starting_number: 1, numerical_width: "", prefill_zero: false, prevent_duplicates: true
    })
    this.show()
  }
}
